import { sum, min, round, allEqual } from './math';

export type Chat = {
	actionType: "chat";
	message: string;
}

export type Offer = {
	actionType: "offer";
	split: number[];
}

export type Showdown = {
	actionType: "showdown";
}

export type Action = Chat | Offer | Showdown;

export type Move = {
	player: string;
	action: Action;
}

export type Cards = {
	hands: number[];
	handicap: number;
}

export type PlayerState = {
	players: string[];
	history: Move[];
	hand: number;
}

export type HouseState = {
	players: string[];
	history: Move[];
	cards: Cards;
}

export type PlayOutcome = {
	showdown: boolean;
	winner: string | null;
	prizes: number[];
	play: HouseState;
}

export function initialHouseState(players: string[]): HouseState {
	return {
		players,
		history: [],
		cards: {
			hands: players.map(_ => Math.random()),
			handicap: Math.random()
		}
	}
}

export function getPlayerStates(play: HouseState): PlayerState[] {
	const {players, history, cards} = play;
	return [...players.keys()].map((i: number) => {
		const hand = cards.hands[i];
		return { players, history, hand }
	})
}

export function isShowdown(play: HouseState | PlayerState): boolean {
	return play.history.some(move => move.action.actionType == "showdown");
}

// Who offered / accepted a split, and in what order?
export function offerOrder(play: HouseState | PlayerState, split: number[]): string[] {
	const isOffer = (move: Move) => move.action.actionType == "offer";
	const offers = play.history.filter(isOffer);
	const matchingOffers = offers.filter(
		(offer: Move) => allEqual(split, (offer.action as Offer).split)
	);
	return matchingOffers.map(offer => offer.player);
}

// Which players offered / accepted a split?
export function whoAccepted(play: HouseState | PlayerState, split: number[]): string[] {
	const coalition = new Set(offerOrder(play, split));
	return [...coalition];
}

// Has an offer been accepted?  If so, which?
// Note: the most recent move must have been an offer acceptance.
export function getAgreement(play: HouseState | PlayerState): number[] | null {
	const T = play.history.length;
	if (T == 0)
		return null;

	const lastAction = play.history[T - 1].action;
	if (lastAction.actionType != "offer")
		return null;

	const target = (lastAction as Offer).split;
	const coalition = whoAccepted(play, target);
	return (coalition.length == play.players.length) ? target : null;
}

export function isGameOver(play: HouseState | PlayerState): boolean {
	return !!getAgreement(play) || isShowdown(play);
}

export function checkMove(play: HouseState | PlayerState, move: Move): boolean {
	if (isGameOver(play))
		return false;

	switch(move.action.actionType) {
		case 'chat':
		case 'showdown':
			return true;

		case 'offer':
			let split = move.action.split;
			return round(sum(split), 5) == 1 && min(split) >= 0;

		default:
			return false;
	}
}

export function houseStateUpdate(play: HouseState, move: Move): HouseState {
	const { players, history, cards } = play;
	if (!checkMove(play, move))
	{
		console.log("Invalid move ", move);
		console.log("in state ", play);
		return play;
	}
	return { players, cards, history: [...history, move] }
}

export function playerStateUpdate(play: PlayerState, move: Move): PlayerState {
	const { players, history, hand } = play;
	if (!checkMove(play, move))
	{
		console.log("Invalid move ", move);
		console.log("in state ", play);
		return play;
	}
	return { players, hand, history: [...history, move] }
}

export function playerStateFromHistory(history: Move[], initialPlay: PlayerState): PlayerState {
	console.log("playerStateFromHistory", initialPlay)
	return history.reduce(playerStateUpdate, initialPlay);
}

export function playWinners(play: HouseState): boolean[] {
	const { hands, handicap } = play.cards;
	const rankedHands = hands.slice().sort((a: number, b: number) => b - a);
	const winners = hands.map(
		(hand: number) => handicap * hand > rankedHands[1]
	);
	return winners;
}

export function playOutcome(play: HouseState): PlayOutcome {
	if (!isGameOver(play))
		console.log("playOutcome() Game isn't over yet!");

	const showdown = isShowdown(play);
	const winners = playWinners(play);
	const winner = play.players[winners.findIndex(win => win)];

	let prizes: number[];

	if (showdown) {
		const n = play.players.length;
		const bonus = 0.1 / n;
		prizes = winners.map((win: boolean) => Number(win) + bonus);
	} else {
		prizes = getAgreement(play)!;
	}

	return { showdown, winner, prizes, play };
}

